// solver.js
// Deadlock detection + fast hint system for Crystal Puzzle.
//
// Works on the same `tubes` structure the game already uses:
//   Array of tubes, each tube an array of { color, size, isRevealed } groups
//   (bottom of tube = index 0, top = last index), i.e. the output of combineColors().
//
// Both features share one move generator so the "no moves" logic and the hint
// logic can never disagree about what a legal/useful move is.

import { getTubeHeight, hasGravityMode } from './gameLogic';

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function tubeSize(tube) {
  let s = 0;
  for (let i = 0; i < tube.length; i++) s += tube[i].size;
  return s;
}

function isSingleColor(tube) {
  // After combineColors, a single-color tube is exactly one group.
  return tube.length === 1;
}

function isCompleteTube(tube, maxHeight) {
  return tube.length === 1 && tube[0].size === maxHeight;
}

/**
 * Canonical key for a state. Tube ORDER does not matter for solvability,
 * so we sort the per-tube strings. This collapses huge numbers of
 * symmetric states and is the main reason the search is fast.
 */
function stateKey(tubes) {
  const parts = new Array(tubes.length);
  for (let i = 0; i < tubes.length; i++) {
    const t = tubes[i];
    let s = '';
    for (let j = 0; j < t.length; j++) s += t[j].color + 'x' + t[j].size + '.';
    parts[i] = s;
  }
  parts.sort();
  return parts.join('|');
}

/**
 * Apply a move the exact same way makeMove() in CrystalPuzzle.js does:
 * pop the whole top group, then push on top (normal) or insert at the
 * bottom (gravity mode), then merge adjacent same-color groups locally.
 * Returns a NEW tubes array (input is not mutated).
 */
export function applyMove(tubes, fromIdx, toIdx, gravity) {
  const next = tubes.map(t => t.slice());
  const moving = next[fromIdx].pop();

  const dest = next[toIdx];
  if (gravity) {
    if (dest.length > 0 && dest[0].color === moving.color) {
      dest[0] = { ...dest[0], size: dest[0].size + moving.size, isRevealed: dest[0].isRevealed || moving.isRevealed };
    } else {
      dest.unshift({ ...moving });
    }
  } else {
    const top = dest[dest.length - 1];
    if (top && top.color === moving.color) {
      dest[dest.length - 1] = { ...top, size: top.size + moving.size, isRevealed: top.isRevealed || moving.isRevealed };
    } else {
      dest.push({ ...moving });
    }
  }
  return next;
}

// ---------------------------------------------------------------------------
// Move generation
// ---------------------------------------------------------------------------

/**
 * All moves that are (a) legal per the game's rules and (b) not provably
 * pointless. Pointless moves that are filtered out:
 *
 *   1. Moving out of a completed tube (single color, full height).
 *      Breaking a finished tube can never be required in this puzzle.
 *   2. Moving a single-color tube's top group into an EMPTY tube.
 *      That just relocates the tube's contents — the resulting position is
 *      identical up to tube ordering, so it can never create progress.
 *   3. (search only, via oneEmptyOnly) Using more than one empty tube as a
 *      destination — all empty tubes are interchangeable, so trying one is
 *      enough. This slashes the branching factor.
 *
 * Because every filtered move maps the position to an equivalent position,
 * "no effective moves" ⇔ "the player is genuinely stuck", which is exactly
 * what we want for the deadlock check.
 *
 * Each returned move: { from, to, score } — score is a heuristic used to
 * order the search (higher = try first).
 */
export function getEffectiveMoves(tubes, level, isHardMode, oneEmptyOnly = false) {
  const maxHeight = getTubeHeight(level, isHardMode);
  const gravity = isHardMode && hasGravityMode(level, isHardMode);
  const moves = [];

  // Precompute sizes and find the first empty tube.
  const sizes = tubes.map(tubeSize);
  let firstEmpty = -1;
  for (let i = 0; i < tubes.length; i++) {
    if (tubes[i].length === 0) { firstEmpty = i; break; }
  }

  for (let from = 0; from < tubes.length; from++) {
    const fromTube = tubes[from];
    if (fromTube.length === 0) continue;
    if (isCompleteTube(fromTube, maxHeight)) continue; // rule 1

    const moving = fromTube[fromTube.length - 1];

    for (let to = 0; to < tubes.length; to++) {
      if (to === from) continue;
      const toTube = tubes[to];
      const destEmpty = toTube.length === 0;

      if (destEmpty) {
        if (isSingleColor(fromTube)) continue;          // rule 2
        if (oneEmptyOnly && to !== firstEmpty) continue; // rule 3
      }

      // --- legality (mirrors isValidMove in gameLogic.js) ---
      if (sizes[to] + moving.size > maxHeight) continue;
      if (!destEmpty) {
        const target = gravity ? toTube[0] : toTube[toTube.length - 1];
        if (target.color !== moving.color) continue;
      }

      // --- heuristic score for move ordering / fallback hints ---
      let score;
      if (destEmpty) {
        // Digging: prefer unearthing from tubes with more buried variety.
        score = 5 + fromTube.length;
      } else {
        const target = gravity ? toTube[0] : toTube[toTube.length - 1];
        const mergedRun = target.size + moving.size;
        score = 50 + mergedRun;
        // Completing a tube is almost always right.
        if (toTube.length === 1 && mergedRun === maxHeight) score += 1000;
        // Emptying the source tube frees up space.
        if (fromTube.length === 1) score += 30;
      }
      moves.push({ from, to, score });
    }
  }

  moves.sort((a, b) => b.score - a.score);
  return moves;
}

/**
 * Deadlock check for the UI. True when the level is not solved and there is
 * no move that can possibly make progress.
 * O(tubes²) — cheap enough to run after every move.
 */
export function isStuck(tubes, level, isHardMode) {
  const maxHeight = getTubeHeight(level, isHardMode);
  const colors = new Set();
  for (const t of tubes) for (const g of t) colors.add(g.color);
  // A solved board also has no effective moves — that's not "stuck".
  if (isSolvedState(tubes, maxHeight, colors.size)) return false;
  return getEffectiveMoves(tubes, level, isHardMode).length === 0;
}

// ---------------------------------------------------------------------------
// Hint solver
// ---------------------------------------------------------------------------

function countSolvedTubes(tubes, maxHeight) {
  let n = 0;
  for (const t of tubes) if (isCompleteTube(t, maxHeight)) n++;
  return n;
}

function isSolvedState(tubes, maxHeight, numColors) {
  return countSolvedTubes(tubes, maxHeight) === numColors;
}

/**
 * Find a hint from the current position.
 *
 * Strategy: depth-first search with
 *   - a transposition table on canonical (order-independent) state keys,
 *   - pointless-move pruning and "single empty destination" pruning,
 *   - greedy move ordering (finish tube > merge > dig),
 *   - a hard time / node budget.
 *
 * The first three are what make this fast where a naive solver takes
 * minutes: they shrink the effective state space by orders of magnitude.
 *
 * Returns:
 *   { status: 'solved',      move, line }  — `move` = {from,to} to show the
 *                                            player; `line` = full winning
 *                                            move sequence (cache it!).
 *   { status: 'unsolvable' }               — search EXHAUSTED all states:
 *                                            no path to victory exists.
 *   { status: 'timeout',     move }        — budget ran out; `move` is the
 *                                            best heuristic move (may be
 *                                            null if truly stuck).
 *   { status: 'stuck' }                    — no effective moves at all.
 *   { status: 'already_solved' }
 *
 * options: { timeBudgetMs = 300, maxNodes = 150000 }
 */
export function findHint(tubes, level, isHardMode, options = {}) {
  const timeBudgetMs = options.timeBudgetMs ?? 300;
  const maxNodes = options.maxNodes ?? 150000;

  const maxHeight = getTubeHeight(level, isHardMode);
  const gravity = isHardMode && hasGravityMode(level, isHardMode);

  const colors = new Set();
  for (const t of tubes) for (const g of t) colors.add(g.color);
  const numColors = colors.size;

  if (isSolvedState(tubes, maxHeight, numColors)) {
    return { status: 'already_solved' };
  }

  const rootMoves = getEffectiveMoves(tubes, level, isHardMode, true);
  if (rootMoves.length === 0) return { status: 'stuck' };

  const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

  const visited = new Set([stateKey(tubes)]);
  let nodes = 0;
  let timedOut = false;

  // Iterative DFS. Stack frames: { tubes, moves, moveIndex, moveTaken }
  // `line` is rebuilt from the stack when a solution is found.
  const stack = [{ tubes, moves: rootMoves, i: 0, taken: null }];

  while (stack.length > 0) {
    const frame = stack[stack.length - 1];

    if (frame.i >= frame.moves.length) {
      stack.pop(); // dead end — backtrack
      continue;
    }

    const mv = frame.moves[frame.i++];

    nodes++;
    if (nodes > maxNodes || (nodes % 512 === 0 && now() - start > timeBudgetMs)) {
      timedOut = true;
      break;
    }

    const nextTubes = applyMove(frame.tubes, mv.from, mv.to, gravity);
    const key = stateKey(nextTubes);
    if (visited.has(key)) continue;
    visited.add(key);

    if (isSolvedState(nextTubes, maxHeight, numColors)) {
      // Reconstruct the winning line from the stack.
      const line = [];
      for (let d = 1; d < stack.length; d++) line.push(stack[d].taken);
      line.push({ from: mv.from, to: mv.to });
      return { status: 'solved', move: line[0], line };
    }

    stack.push({
      tubes: nextTubes,
      moves: getEffectiveMoves(nextTubes, level, isHardMode, true),
      i: 0,
      taken: { from: mv.from, to: mv.to },
    });
  }

  if (!timedOut) {
    // We explored every reachable state without finding a solution.
    return { status: 'unsolvable' };
  }

  // Budget exhausted — fall back to the best-looking immediate move so the
  // player still gets *something* useful instantly.
  return { status: 'timeout', move: { from: rootMoves[0].from, to: rootMoves[0].to } };
}

// ---------------------------------------------------------------------------
// HintManager — caches the winning line between hint requests
// ---------------------------------------------------------------------------

/**
 * Wraps findHint with a cache of the last computed winning line.
 * If the player follows the hints (or plays the moves we predicted),
 * subsequent hints are O(1) instead of re-searching.
 *
 * Usage in the component:
 *   const hintManager = useRef(new HintManager()).current;
 *   ...
 *   const hint = hintManager.getHint(containers, currentLevel, isHardMode);
 *   hintManager.invalidate() on restart / undo / level change / Add Tube.
 */
export class HintManager {
  constructor() {
    this.cachedLine = null;   // remaining winning moves
    this.expectedKey = null;  // state key the next cached move applies to
    this.gravity = false;
  }

  invalidate() {
    this.cachedLine = null;
    this.expectedKey = null;
  }

  getHint(tubes, level, isHardMode, options) {
    // Fast path: player is on the line we already computed.
    if (this.cachedLine && this.cachedLine.length > 0 && stateKey(tubes) === this.expectedKey) {
      const move = this.cachedLine[0];
      return { status: 'solved', move, line: this.cachedLine };
    }

    const result = findHint(tubes, level, isHardMode, options);

    if (result.status === 'solved') {
      const gravity = isHardMode && hasGravityMode(level, isHardMode);
      this.cachedLine = result.line;
      this.expectedKey = stateKey(tubes);
      this.gravity = gravity;
    } else {
      this.invalidate();
    }
    return result;
  }

  /**
   * Call after every player move so the cache follows along.
   * If the player played exactly the predicted move, advance the line;
   * otherwise drop the cache (next hint re-searches).
   */
  onPlayerMove(prevTubes, fromIdx, toIdx, newTubes) {
    if (!this.cachedLine || this.cachedLine.length === 0) return;
    const expectedMove = this.cachedLine[0];
    if (stateKey(prevTubes) === this.expectedKey &&
        expectedMove.from === fromIdx && expectedMove.to === toIdx) {
      this.cachedLine = this.cachedLine.slice(1);
      this.expectedKey = stateKey(newTubes);
    } else {
      this.invalidate();
    }
  }
}
